import styled from "styled-components";
import { theme } from "../design-system/theme";

const bp = theme.breakpoints || { md: "768px", lg: "1024px" };

/**
 * V2 Layout – Command Center theme, viewport-fixed, sidebar and content scroll independently.
 * Responsive: sidebar becomes overlay drawer below md; hamburger in header toggles it.
 */
export const Wrapper = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  font-family: ${theme.typography.fontFamily};
  position: relative;

  .sidebar-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 90;
    @media (max-width: ${bp.md}) {
      display: block;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
    }
  }
  .sidebar-overlay[aria-hidden="false"] {
    @media (max-width: ${bp.md}) {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .sidebar {
    flex: 0 0 220px;
    width: 220px;
    padding: 24px 0 24px 0;
    background-color: ${theme.sidebar.background};
    border-right: ${theme.sidebar.borderRight};
    box-shadow: none;
    z-index: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: visible;

    /* V2: logo + user profile static, nav scrollable */
    > * {
      flex: 1 1 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    > * .logo,
    > * .user-info {
      flex-shrink: 0;
    }
    /* V2: visual separation between profile (static) and scrollable nav */
    > * .user-info {
      border-bottom: 1px solid ${theme.colors.accent};
      margin-bottom: 20px;
      padding-bottom: 16px;
    }
    /* V2 theme: user profile area */
    > * .user-info .item-select .select__control {
      background-color: ${theme.colors.backgroundAlt} !important;
      border-color: ${theme.colors.accent} !important;
    }
    > * .user-info .user-logout p:hover {
      background-color: ${theme.colors.accentLight} !important;
    }
    /* V2 theme: nav active/hover states – purple accent */
    .navlist > li > .nav-link.nav-box.active,
    .navlist > li > .nav-link.active,
    .navlist > li.active .nav-box {
      background: ${theme.sidebar.activeBg} !important;
      border: 1px solid ${theme.sidebar.activeBorder} !important;
      box-shadow: none !important;
    }
    .navlist > li.active .nav-box *,
    .navlist > li > .nav-link.active .label {
      color: ${theme.colors.accent} !important;
    }
    .navlist > li > .nav-link:hover,
    .navlist > li .nav-box:hover {
      border-color: ${theme.sidebar.activeBorder} !important;
      background: ${theme.sidebar.hoverBg} !important;
    }
    /* V2 Navigation: Nav fills space, scroll on inner wrapper */
    > * nav.nav-v2 {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: visible;
    }
    > * .nav-scroll-area {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }
    /* Legacy: when using V1 Navigation, scroll on nav */
    > * > nav:not(.nav-v2) {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }
    @media (max-width: ${bp.md}) {
      position: fixed;
      left: 0;
      top: 0;
      bottom: 0;
      height: 100vh;
      z-index: 100;
      transform: translateX(-100%);
      transition: transform 0.25s ease;
      box-shadow: none;
    }
  }
  .sidebar.sidebar-open {
    @media (max-width: ${bp.md}) {
      transform: translateX(0);
      box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
    }
  }

  .main-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: ${theme.colors.backgroundAlt};

    &.welcome-page {
      position: relative;
    }

    /* V2 header bar: translucent theme color, page title left, actions right */
    .v2-header-bar {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      height: 64px;
      min-height: 64px;
      padding: 0 ${theme.spacing.xl};
      border-bottom: 1px solid rgba(57, 0, 149, 0.2);
      background: linear-gradient(
        120deg,
        rgba(57, 0, 149, 0.14) 0%,
        rgba(57, 0, 149, 0.1) 50%,
        rgba(102, 48, 210, 0.08) 100%
      );
      box-shadow: 0 2px 12px rgba(57, 0, 149, 0.08);
      gap: ${theme.spacing.sm};
    }
    @media (max-width: ${bp.md}) {
      .v2-header-bar {
        padding: 0 ${theme.spacing.md};
      }
    }
    .v2-header-bar .v2-header-menu {
      display: none;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      border: 1px solid rgba(57, 0, 149, 0.25);
      border-radius: ${theme.borderRadius.lg};
      background: rgba(255, 255, 255, 0.9);
      color: ${theme.colors.accent};
      cursor: pointer;
      flex-shrink: 0;
      @media (max-width: ${bp.md}) {
        display: flex;
      }
    }
    .v2-header-bar .v2-header-menu:hover {
      background: #fff;
      border-color: ${theme.colors.accent};
    }
    .v2-header-bar .v2-page-title {
      margin: 0;
      font-family: ${theme.typography.fontFamily};
      font-size: 24px;
      font-weight: ${theme.typography.fontWeight.semibold};
      color: ${theme.colors.primary};
      flex-shrink: 0;
      margin-right: ${theme.spacing.lg};
      @media (max-width: ${bp.sm || "640px"}) {
        font-size: 16px;
        margin-right: ${theme.spacing.sm};
      }
    }
    .v2-header-bar .v2-header-back {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      margin-right: ${theme.spacing.md};
      padding: 0;
      border: 1px solid rgba(57, 0, 149, 0.25);
      border-radius: ${theme.borderRadius.lg};
      background: rgba(255, 255, 255, 0.9);
      color: ${theme.colors.accent};
      cursor: pointer;
      transition: background 0.2s, border-color 0.2s, transform 0.2s;
    }
    .v2-header-bar .v2-header-back:hover {
      background: #fff;
      border-color: ${theme.colors.accent};
      transform: scale(1.05);
    }
    .v2-header-bar > header {
      flex: 1;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      height: 100% !important;
      min-height: 0 !important;
      padding: 0 !important;
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
    }
    .v2-header-bar .icon-container {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      justify-content: center !important;
      gap: ${theme.spacing.sm};
      margin-left: auto;
    }
    .v2-header-bar .icon-container span {
      width: 40px !important;
      height: 40px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      background: rgba(255, 255, 255, 0.9) !important;
      border-radius: ${theme.borderRadius.lg};
      border: 1px solid ${theme.colors.border};
      box-shadow: 0 1px 3px rgba(57, 0, 149, 0.08);
      cursor: pointer;
      transition: background 0.2s, transform 0.2s;
    }
    .v2-header-bar .icon-container span:hover {
      background: #fff !important;
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(57, 0, 149, 0.12);
    }
    /* Welcome page: transparent bar, icons in dark pill over image */
    &.welcome-page .v2-header-bar {
      background: transparent !important;
      border-bottom: none !important;
      box-shadow: none !important;
    }
    &.welcome-page .v2-header-bar .icon-container {
      background: rgba(14, 18, 29, 0.6) !important;
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      padding: 8px 12px;
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
    }
    &.welcome-page .v2-header-bar .icon-container span {
      width: 36px !important;
      height: 36px !important;
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
    }
    &.welcome-page .v2-header-bar .icon-container span img,
    &.welcome-page .v2-header-bar .icon-container span svg {
      filter: brightness(0) invert(1);
      opacity: 0.95;
    }
    &.welcome-page .v2-header-bar .icon-container span:hover {
      background: rgba(255, 255, 255, 0.15) !important;
    }
  }

  .copyrights {
    flex-shrink: 0;
    padding: 24px;
  }

  /* Welcome page: copyrights fixed at bottom, above the full-screen image */
  .main-content.welcome-page .copyrights {
    margin-top: auto;
    position: relative;
    z-index: 2;
  }
`;

export const ContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 16px;
  @media (max-width: ${bp.md}) {
    padding: 12px;
  }
  @media (max-width: ${bp.sm || "640px"}) {
    padding: 8px;
  }

  &.welcome-page {
    position: absolute;
    inset: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 0;
  }
`;
